import { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useTicketTabs = ({ selectedQueueIds, showAll, skipCounts } = {}) => {
  const [tabs, setTabs] = useState([]);
  const [tabCounts, setTabCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, socket } = useContext(AuthContext);
  const debouncedFetchRef = useRef(null);

  const queueIdsKey = useMemo(
    () => (Array.isArray(selectedQueueIds) ? JSON.stringify(selectedQueueIds) : ""),
    [selectedQueueIds]
  );
  const fetchTabs = useCallback(async () => {
    if (!user?.companyId) return;
    try {
      const { data } = await api.get("/ticket-tabs");
      setTabs(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.companyId]);

  const fetchCounts = useCallback(async () => {
    if (!user?.companyId || skipCounts) return;
    try {
      const params = {};
      if (queueIdsKey) {
        params.queueIds = queueIdsKey;
      }
      if (showAll != null) {
        params.showAll = showAll ? "true" : "false";
      }
      const { data } = await api.get("/ticket-tabs/counts", { params });
      setTabCounts(data);
    } catch (err) {
      // silencioso
    }
  }, [user?.companyId, queueIdsKey, showAll, skipCounts]);

  const debouncedFetchCounts = useCallback(() => {
    if (skipCounts) return;
    if (debouncedFetchRef.current) clearTimeout(debouncedFetchRef.current);
    debouncedFetchRef.current = setTimeout(() => {
      fetchCounts();
    }, 800);
  }, [fetchCounts, skipCounts]);

  useEffect(() => {
    return () => {
      if (debouncedFetchRef.current) clearTimeout(debouncedFetchRef.current);
    };
  }, []);

  useEffect(() => {
    fetchTabs();
    if (!skipCounts) fetchCounts();
  }, [fetchTabs, fetchCounts, skipCounts]);

  useEffect(() => {
    if (skipCounts) return;
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts, skipCounts]);

  useEffect(() => {
    if (!user?.companyId) return;

    const companyId = user.companyId;
    if (!socket) return;

    const handleTabEvent = (data) => {
      if (data.action === "create" && data.ticketTab) {
        setTabs((prev) => [...prev, data.ticketTab]);
      } else if (data.action === "update" && data.ticketTab) {
        setTabs((prev) =>
          prev.map((t) => (t.id === data.ticketTab.id ? data.ticketTab : t))
        );
      } else if (data.action === "delete" && data.ticketTabId) {
        setTabs((prev) => prev.filter((t) => t.id !== data.ticketTabId));
      } else if (data.action === "reorder") {
        setTabs(data.ticketTabs || []);
      }
    };

    const handleTicketEvent = (data) => {
      if (data.action === "update" || data.action === "create" || data.action === "delete") {
        debouncedFetchCounts();
      }
    };

    const handleAppMessageEvent = (data) => {
      if (data.action === "create" && !data.message?.fromMe) {
        debouncedFetchCounts();
      }
    };

    const tabEventName = `company-${companyId}-ticketTab`;
    const ticketEventName = `company-${companyId}-ticket`;
    const appMsgEventName = `company-${companyId}-appMessage`;

    socket.on(tabEventName, handleTabEvent);
    if (!skipCounts) {
      socket.on(ticketEventName, handleTicketEvent);
      socket.on(appMsgEventName, handleAppMessageEvent);
    }

    return () => {
      socket.off(tabEventName, handleTabEvent);
      if (!skipCounts) {
        socket.off(ticketEventName, handleTicketEvent);
        socket.off(appMsgEventName, handleAppMessageEvent);
      }
    };
  }, [user?.companyId, debouncedFetchCounts, skipCounts, socket]);

  const getCountForTab = useCallback(
    (tabId) => {
      const found = tabCounts.find((tc) => tc.id === tabId);
      return found || { total: 0, unread: 0 };
    },
    [tabCounts]
  );

  const totalUnreadOtherTabs = useCallback(
    (currentTabId) => {
      return tabCounts.reduce((sum, tc) => {
        if (tc.id !== currentTabId) return sum + tc.unread;
        return sum;
      }, 0);
    },
    [tabCounts]
  );

  return {
    tabs,
    tabCounts,
    loading,
    fetchTabs,
    fetchCounts,
    getCountForTab,
    totalUnreadOtherTabs,
  };
};

export default useTicketTabs;
